import React, { useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { colors, typography } from '../../../../styles/theme';
import {
  getOwnExtraUserLots,
  indexUserLotsById,
} from '../../../utils/lotFunctionalUnits';
import { LotInfoCollapseSection, SECTION_BAR_ICON_BUTTON_SX } from './formBlockUi';
import OfficialUnitsList from './OfficialUnitsList';
import UnitListView from './UnitListView';
import CommercialRollupDetails from '../../../../Leads/components/CommercialRollupDetails';

const sectionCountSx = {
  fontFamily: typography.fontFamily.primary,
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '16px',
  color: colors.textSecondary,
  flexShrink: 0,
};

const SectionCount = ({ value }) => (
  <Typography component="span" sx={sectionCountSx}>
    {value}
  </Typography>
);

const AddOwnUnitButton = ({ onClick }) => (
  <Tooltip title="Agregar" placement="top" arrow>
    <IconButton
      size="small"
      onClick={onClick}
      aria-label="Agregar unidad propia"
      sx={SECTION_BAR_ICON_BUTTON_SX}
    >
      <AddIcon />
    </IconButton>
  </Tooltip>
);

/**
 * Tab Unidades: bloques colapsables al estilo del detalle de lote
 * (Información principal). Oficiales y propias no se mezclan; el listado
 * catastral scrollea adentro para no tapar las UF del usuario.
 */
const UnitsTabList = ({
  officialUnits = [],
  userUnits = [],
  /** `commercial_rollup` de getuserlotunits (lote + UF). Solo dueño / editor. */
  commercialRollup = null,
  /** `lot_commercial_summary`: su etapa suma al desglose. */
  commercialLotSummary = null,
  userLotLists = [],
  canEdit = false,
  readOnly = false,
  hideListFilter = false,
  showAddUnit = true,
  accentColor = null,
  onSelectUnit,
  onAddOwnUnit,
  onCargarInfo,
  onEditOverlay,
  onRemoveOverlay,
  /** Espacio ajeno: Eliminar del overlay solo con `can_delete` de la API. */
  removeOverlayRequiresCanDelete = false,
  /** UF recién creada desde el wizard: resaltado temporal en el listado. */
  highlightUnitId = null,
}) => {
  const overlayByUserLotId = useMemo(
    () => indexUserLotsById(userUnits),
    [userUnits],
  );
  const ownExtraUnits = useMemo(
    () => getOwnExtraUserLots(userUnits),
    [userUnits],
  );
  const canAddOwn = canEdit && showAddUnit && !readOnly && Boolean(onAddOwnUnit);

  const unitListProps = {
    userLotLists,
    onSelectUnit,
    canEdit,
    hideListFilter,
    showAddUnit: false,
    publicView: readOnly,
    accentColor,
    embedded: true,
    highlightUnitId,
  };
  const ownCollapseActions = canAddOwn
    ? <AddOwnUnitButton onClick={onAddOwnUnit} />
    : null;

  const rollupNode = !readOnly && commercialRollup ? (
    <CommercialRollupDetails rollup={commercialRollup} lotSummary={commercialLotSummary} sx={{ px: 2, pb: 1.5 }} />
  ) : null;

  if (!officialUnits.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', pt: 2, pb: 1 }}>
        {rollupNode}
        <LotInfoCollapseSection
          title="Unidades"
          titleAdornment={<SectionCount value={userUnits.length} />}
          showCollapseToggle={!canAddOwn}
          actions={ownCollapseActions}
        >
          <UnitListView units={userUnits} {...unitListProps} />
        </LotInfoCollapseSection>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: 2, pb: 1 }}>
      {rollupNode}
      <LotInfoCollapseSection
        title="Unidades catastrales"
        titleAdornment={<SectionCount value={officialUnits.length} />}
        defaultExpanded
      >
        <OfficialUnitsList
          units={officialUnits}
          overlayByUserLotId={overlayByUserLotId}
          canEdit={canEdit && !readOnly}
          onCargarInfo={onCargarInfo}
          onSelectOverlay={onSelectUnit}
          onEditOverlay={onEditOverlay}
          onRemoveOverlay={onRemoveOverlay}
          removeOverlayRequiresCanDelete={removeOverlayRequiresCanDelete}
          highlightUnitId={highlightUnitId}
        />
      </LotInfoCollapseSection>

      <LotInfoCollapseSection
        title="Unidades propias"
        titleAdornment={<SectionCount value={ownExtraUnits.length} />}
        defaultExpanded
        showCollapseToggle={!canAddOwn}
        actions={ownCollapseActions}
      >
        <UnitListView
          units={ownExtraUnits}
          {...unitListProps}
          emptyHint="Todavía no hay unidades propias en este lote. Podés agregar una sin vincularla a una UF catastral."
        />
      </LotInfoCollapseSection>
    </Box>
  );
};

export default UnitsTabList;
